import React, { ReactNode } from 'react'

import { TCustomizedMetric } from 'types/GlobalMetric'

import { TAnalysisType } from 'redux/hooks/AnalysisTypes'
import { useAttributeOption } from 'redux/hooks/AttributeOptions'
import { useMetricData } from 'redux/hooks/MetricData'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import Link from 'components_new/atoms/Link'
import Skeleton from 'components_new/atoms/Skeleton'
import Text from 'components_new/atoms/Text'

import GenericListItem from 'components_new/molecules/GenericListItem'
import IconTip from 'components_new/molecules/IconTip'
import IconButton from 'components_new/molecules/IconButton'
import LineChart from 'components_new/molecules/charts/LineChart'
import TimeSinceLabel from 'components_new/molecules/TimeSinceLabel'
import ValueText from 'components_new/molecules/ValueText'

import {
  getIncreaseIsPositive,
  getLabels,
  getLineColor,
  getMetricTitle,
  getValues
} from 'utils/metric'
import { useAnalysisTypeGenerating } from 'redux/hooks/AnalysisGenerations'
import { useAnalysisTypeAnalyses } from 'redux/hooks/Analyses'

export const POTENTIAL_UNIT = 'kr/år'

/**
 * Every cell that shows metric data reads it through this hook. The data
 * lives in the store and the fetch thunks dedupe in-flight requests, so
 * several cells in one row share a single request.
 */
const useAnalysisMetric = (customizedMetric: TCustomizedMetric) => {
  const { lang, translateEnum } = useTranslation()

  const { metric, missingSlices, rawData, error } =
    useMetricData(customizedMetric)
  const segmentOption = useAttributeOption(rawData?.segment_attribute_option_id)

  const loading = !error && (!metric || !rawData)

  const labels = getLabels({ lang, rawData, segmentOption })
  const values = getValues({ rawData }, error)
  const nonNullValues = values.filter((value) => value !== null)
  const currentValue = nonNullValues[nonNullValues.length - 1] ?? null

  const metricTitle = metric
    ? getMetricTitle(metric, customizedMetric.settings, lang, translateEnum)
    : null

  return {
    currentValue,
    error,
    labels,
    loading,
    metric,
    metricTitle,
    missingSlices,
    values
  }
}

/**
 * Stands in for the metric cells when the analysis type has no follow-up
 * metric.
 */
export const MissingMetricCell = () => {
  const { t } = useTranslation()

  return (
    <Text color="text.disabled" variant="body2">
      {t('global', 'missing')}
    </Text>
  )
}

type TTitleCellProps = {
  analysisType: TAnalysisType
  onRun: (id: string) => void
}

/*-- analysis type / metric --*/
export const TitleCell = (
  props: TTitleCellProps & { metric: TCustomizedMetric | null }
) => {
  const { metric, ...rest } = props
  const { t } = useTranslation()

  return metric ? (
    <MetricTitleCell {...rest} metric={metric} />
  ) : (
    <TitleCellContent
      {...rest}
      metricTitle={<em>{t('analysis_types', 'no_follow_up')}</em>}
    />
  )
}

/** Reads the metric title here, since the hook can't run without a metric. */
const MetricTitleCell = (
  props: TTitleCellProps & { metric: TCustomizedMetric }
) => {
  const { metric, ...rest } = props
  const { metricTitle } = useAnalysisMetric(metric)

  return <TitleCellContent {...rest} metricTitle={metricTitle ?? ''} />
}

const TitleCellContent = (
  props: TTitleCellProps & { metricTitle: ReactNode }
) => {
  const { analysisType, metricTitle, onRun } = props
  const { t } = useTranslation()

  const currentlyRunningAnalysisTypes = useAnalysisTypeGenerating()
  const isRunning = Boolean(currentlyRunningAnalysisTypes[analysisType.id])
  const { lastPublishedAt } = useAnalysisTypeAnalyses(analysisType.id)

  if (!analysisType) return <Skeleton variant="text" width={160} />

  return (
    <Box sx={{ display: 'flex' }}>
      <GenericListItem
        actions={
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexWrap: 'nowrap',
              gap: 1,
              ml: 2
            }}
          >
            <Text color="text.secondary" noWrap={true} variant="caption">
              {isRunning ? (
                `${t('analysis_types', 'run_loading_title')}...`
              ) : lastPublishedAt ? (
                <TimeSinceLabel
                  sx={{ color: 'text.secondary' }}
                  value={lastPublishedAt}
                />
              ) : (
                t('analysis_types', 'no_runs')
              )}
            </Text>
            <IconButton
              color={isRunning ? 'brand' : 'primary'}
              disabled={!analysisType.active}
              loading={isRunning}
              loadingTitle={t('analysis_types', 'run_loading_title')}
              onClick={() => onRun(analysisType.id)}
              name={'PlayCircleFilledWhite'}
              size="small"
              title={t('analysis_types', 'run_analysis')}
              variant="outlined"
            />
          </Box>
        }
        body={metricTitle}
        disablePadding={true}
        customTitle={
          <Link href={`/analysis-types/${analysisType.id}`}>
            {analysisType.name}
          </Link>
        }
        title={analysisType.name}
        variant="analysis_type"
      />
    </Box>
  )
}

/*-- metric --*/
export const MetricCell = (props: {
  metric: TCustomizedMetric
  onViewMetric: (metric: TCustomizedMetric) => void
}) => {
  const { metric: customizedMetric, onViewMetric } = props

  const { t } = useTranslation()
  const {
    currentValue,
    error,
    labels,
    loading,
    metric,
    missingSlices,
    values
  } = useAnalysisMetric(customizedMetric)

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        justifyContent: 'flex-end'
      }}
    >
      <Box
        onClick={(e) => {
          e.stopPropagation()
          onViewMetric(customizedMetric)
        }}
        sx={{
          alignItems: 'flex-end',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          p: '6px 16px',
          width: '100%',
          ['&:hover']: {
            bgcolor: 'action.hover',
            cursor: 'zoom-in'
          }
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: '100%'
          }}
        >
          {loading ? (
            <Skeleton height={24} variant="rounded" />
          ) : (
            <LineChart
              data={{
                labels: labels,
                isTimeseries: true,
                datasets: [
                  {
                    color: getLineColor({
                      increaseIsPositive: getIncreaseIsPositive({ metric }),
                      values: values
                    }),
                    label: '',
                    type: 'line',
                    unit: metric?.unit ?? null,
                    values: values
                  }
                ]
              }}
              variant="minimal"
            />
          )}
        </Box>

        <ValueText
          color={getLineColor({
            increaseIsPositive: getIncreaseIsPositive({ metric }),
            values: values
          })}
          error={
            error
              ? missingSlices.length > 0
                ? t('results', 'error_slice_not_activated')
                : t('global', 'error_500_title')
              : undefined
          }
          loading={loading}
          unit={metric?.unit}
          unitPrefixes={metric?.unit_prefixes}
          value={currentValue}
          sx={{ lineHeight: 1, maxWidth: '100%' }}
          variant="caption"
        />
      </Box>
    </Box>
  )
}

/**
 * What the potential cells show, for an analysis type or for the retired
 * ones.
 */
type TPotentials = Pick<
  TAnalysisType,
  'analyses' | 'tasks' | 'discovered_potential' | 'active_potential'
>

/*-- retired analysis types --*/
/** The row that gathers what is left of analysis types taken out of use. */
export const RetiredTitleCell = () => {
  const { t } = useTranslation()

  return (
    <Box sx={{ display: 'flex' }}>
      <GenericListItem
        body={<em>{t('analysis_types', 'no_follow_up')}</em>}
        disablePadding={true}
        icon={{ name: 'RetiredAnalysisType' }}
        title={t('results', 'retired_packages')}
        titleAdornment={
          <IconTip
            size="xsmall"
            sx={{ ml: 0.75 }}
            title={t('results', 'retired_packages_tooltip')}
          />
        }
        TitleProps={{ color: 'text.secondary' }}
      />
    </Box>
  )
}

/*-- unhandled potential --*/
export const UnhandledCell = (props: {
  onViewAnalyses: (ids: string[]) => void
  potentials: TPotentials
}) => {
  const { onViewAnalyses, potentials } = props
  const { t } = useTranslation()

  const analysisIds = potentials.analyses
    .filter((analysis) => !analysis.is_handled)
    .map((analysis) => analysis.id)
  const hasAnalyses = analysisIds.length > 0

  return (
    <Box
      onClick={hasAnalyses ? () => onViewAnalyses(analysisIds) : undefined}
      sx={
        hasAnalyses
          ? {
              cursor: 'pointer',
              ['&:hover > .CountLabel']: { textDecoration: 'underline' },
              ['&:hover > .ValueLabel']: { textDecorationStyle: 'solid' }
            }
          : undefined
      }
    >
      {hasAnalyses ? (
        <Text className="CountLabel" component="div" variant="caption">
          <Icon
            color="inherit"
            fontSize="inherit"
            name="Analyses"
            sx={{ verticalAlign: '-0.1em' }}
          />{' '}
          {analysisIds.length}{' '}
          {t(
            'analyses',
            analysisIds.length === 1 ? 'analysis' : 'analyses',
            true
          )}
        </Text>
      ) : null}
      <ValueText
        className="ValueLabel"
        color={
          potentials.discovered_potential === 0
            ? 'text.disabled'
            : 'success.text'
        }
        displayPositiveIndicator={true}
        onClick={hasAnalyses ? () => {} : undefined}
        unit={POTENTIAL_UNIT}
        value={potentials.discovered_potential}
      />
    </Box>
  )
}

/*-- active potential --*/
interface ActiveCellProps {
  onViewTasks: (ids: string[]) => void
  potentials: TPotentials
}

export const ActiveCell = (props: ActiveCellProps) => {
  const { onViewTasks, potentials } = props
  const { t } = useTranslation()

  const taskIds = potentials.tasks.map((task) => task.id)
  const hasTasks = taskIds.length > 0

  return (
    <Box
      onClick={hasTasks ? () => onViewTasks(taskIds) : undefined}
      sx={
        hasTasks
          ? {
              cursor: 'pointer',
              ['&:hover > .CountLabel']: { textDecoration: 'underline' },
              ['&:hover > .ValueLabel']: { textDecorationStyle: 'solid' }
            }
          : undefined
      }
    >
      {hasTasks ? (
        <Text className="CountLabel" component="div" variant="caption">
          <Icon
            color="inherit"
            fontSize="inherit"
            name="Tasks"
            sx={{ verticalAlign: '-0.1em' }}
          />{' '}
          {taskIds.length}{' '}
          {t('tasks', taskIds.length === 1 ? 'task' : 'tasks', true)}
        </Text>
      ) : null}
      <ValueText
        className="ValueLabel"
        color={
          potentials.active_potential === 0 ? 'text.disabled' : 'success.text'
        }
        displayPositiveIndicator={true}
        onClick={hasTasks ? () => {} : undefined}
        unit={POTENTIAL_UNIT}
        value={potentials.active_potential}
      />
    </Box>
  )
}
